import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Controller, FieldError, useForm } from "react-hook-form";

import * as styles from "./SchedulePicker.styles";
import {
  DAYS,
  type FormValues,
  getInitialValuesFromCron,
  HOURS,
  MINUTES,
  type SchedulePickerProps,
  toCron,
  type WeekDayKey,
} from "./SchedulePicker.utils";

const SchedulePicker: React.FC<SchedulePickerProps> = ({ onSubmit, layout, cronExpression }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: getInitialValuesFromCron(cronExpression),
  });

  const isModalLayout = layout === "modal";

  const onHandleSubmit = async (values: FormValues) => {
    await onSubmit(toCron(values));
    reset(getInitialValuesFromCron());
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onHandleSubmit)} sx={styles.wrapperSx}>
      <Box>
        <Typography sx={styles.sectionLabelSx}>Days</Typography>
        <Controller
          name="days"
          control={control}
          // Based on this validation, errors.days is of type FieldError, not the inferred
          // FieldError[]
          rules={{ validate: (v) => v.length > 0 || "Select at least one day" }}
          render={({ field }) => (
            <ToggleButtonGroup
              value={field.value}
              onChange={(_, next: WeekDayKey[]) => field.onChange(next)}
              sx={styles.dayGroupSx}
            >
              {(Object.keys(DAYS) as WeekDayKey[]).map((key) => (
                <ToggleButton key={key} value={key} sx={styles.dayButtonSx}>
                  {DAYS[key].label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        />
        {errors.days && (
          <Typography sx={styles.errorTextSx}>
            {/* See comment above about the type of errors.days, forcing it to the correct type here */}
            {(errors.days as unknown as FieldError).message as string}
          </Typography>
        )}
      </Box>

      <Box>
        <Typography sx={styles.sectionLabelSx}>Time</Typography>
        <Box sx={styles.timeRowSx}>
          <Controller
            name="hour"
            control={control}
            render={({ field }) => (
              <FormControl size="small" sx={styles.hourSelectSx}>
                <InputLabel>Hour</InputLabel>
                <Select {...field} label="Hour">
                  {HOURS.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="minute"
            control={control}
            render={({ field }) => (
              <FormControl size="small" sx={styles.minuteSelectSx}>
                <InputLabel>Min</InputLabel>
                <Select {...field} label="Min">
                  {MINUTES.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="amPm"
            control={control}
            render={({ field }) => (
              <FormControl size="small" sx={styles.amPmSelectSx}>
                <InputLabel>AM/PM</InputLabel>
                <Select {...field} label="AM/PM">
                  <MenuItem value="AM">AM</MenuItem>
                  <MenuItem value="PM">PM</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          {!isModalLayout && (
            <Button type="submit" variant="contained" disableElevation sx={styles.submitButtonSx}>
              Create New Schedule
            </Button>
          )}
        </Box>

        {isModalLayout && (
          <Box sx={styles.modalActionsSx}>
            <Button type="submit" variant="contained" disableElevation sx={styles.modalButtonSx}>
              Update Schedule
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SchedulePicker;
